import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getBackendService, setProject } from '../../../actions/infrastructures';
import { Settings, Globe, Activity, FileText, UploadCloud, FileJson} from 'lucide-react';
import { BASE_URL } from '../../../config/api';
import General from './Tabs/General';
import Metrics from './Tabs/Metrics';
import Deployment from './Tabs/Deployment';
import Logs from './Tabs/Logs';
import Domain from './Tabs/Domain';
import EnvVar from './Tabs/EnvVar';

const ServicePage = () => {
  const { projectId, serviceId } = useParams();
  const [activeTab, setActiveTab] = useState('general');
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getBackendService(projectId, serviceId));
  }, [dispatch, projectId, serviceId]);

  useEffect(()=>{
    dispatch(setProject(projectId));
  },[dispatch, projectId, serviceId])

  const serviceBasicInfo = useSelector(state => state?.infrastructures?.currentService);
  const clusterName = serviceBasicInfo?.clusterName;
  const serviceName = serviceBasicInfo?.serviceName !=="" ? serviceBasicInfo?.serviceName : `${serviceBasicInfo?.gitHubRepo}-${serviceBasicInfo?.branchUsed}`;
   
  const currentProject = useSelector(state => state?.infrastructures?.currentProject);
  const projectName = `${currentProject?.projectName}-${currentProject?.environment}`
  const tabs = [
    { id: 'general', name: 'General', icon: Settings },
    { id: 'envvar', name: 'Environment Variables', icon: FileJson },
    { id: 'metrics', name: 'Metrics', icon: Activity },
    { id: 'deployment', name: 'Deployment', icon: UploadCloud },
    { id: 'logs', name: 'Logs', icon: FileText },
    { id: 'domain', name: 'Domain', icon: Globe },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return <General serviceBasicInfo={serviceBasicInfo} />;
      case 'metrics':
        return <Metrics serviceId={serviceId} clusterName={clusterName} serviceName={serviceName} />;
      case 'logs':
        return <Logs/>;
      case 'domain':
        return (
          <Domain
            domains={serviceBasicInfo?.domains}
          />
        );
      case 'deployment':
        return <Deployment serviceBasicInfo={serviceBasicInfo}/>;
      case 'envvar':
        return <EnvVar projectName={projectName} gitHubRepo={serviceBasicInfo?.gitHubRepo} branchUsed={serviceBasicInfo?.branchUsed}/>
      default:
        return null;
    }
  };

  if (!serviceBasicInfo) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8 bg-gradient-to-b from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 font-display mb-2">
            {serviceBasicInfo?.gitHubRepo}-{serviceBasicInfo?.branchUsed}
          </h1>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6" aria-label="Tabs">
              {tabs?.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2
                      ${activeTab === tab.id
                        ? 'border-indigo-600 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }
                    `}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{tab.name}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default ServicePage;